/* eslint react/no-danger:0 */
import PropTypes from 'prop-types';
import React, {PureComponent} from 'react';
import classNames from 'classnames';
import debounce from 'lodash.debounce';
import './FormTextArea.less';

/**
 * FormTextArea Component
 **/

// the values are used for computations related to autoExpand functionality
// these are default values that will be override upon componentDidMount if
// autoExpand feature is enabled, using the computesStyles from the textarea
const DEFAULT_COMPUTED_STYLE_VALUES = {
    borderWidth: 1,
    lineHeight: 24,
    maximumHeight: 0,
    paddingBottom: 8,
    paddingTop: 34
};

class FormTextArea extends PureComponent {
    static displayName = 'FormTextArea';

    static propTypes = {
        /** The textarea's id, required for a11y */
        id: PropTypes.string.isRequired,
        /** The textarea's label, required for a11y */
        label: PropTypes.string.isRequired,
        /** The actual content of the textarea */
        value: PropTypes.string,
        /** The height, in number of lines, to make the textarea. This is also the minimum size for autoExpand textareas */
        rows: PropTypes.number,
        /** A flag to make the textarea control expand its height to fit the content */
        autoExpand: PropTypes.bool,
        /** The height as a string, in a CSS unit (px, em, vh...) to limit the textarea's height. Can be left blank, zero, or negative for no limit. */
        maxHeight: PropTypes.string,
        /** The maximum height, in number of lines, to make the textarea. This will be trumped by maxHeight */
        maxRows: PropTypes.number,
        /** For form controls with no visible label, set this to true. Make sure to still add a value to the label property. For screen readers. */
        srOnlyLabel: PropTypes.bool,
        /** The help message displayed beneath the input. */
        helpText: PropTypes.node,
        /** The error message displayed beneath the input. Styling of the input will visually indicate an error state. */
        errorMsg: PropTypes.node,
        /** Flag to make helpText and errorMsg use white text for better a11y contrast when used on dark backgrounds */
        inverse: PropTypes.bool,
        /** Called when the value property changes */
        onChange: PropTypes.func,
        /** Called when the textarea gains focus */
        onFocus: PropTypes.func,
        /** Called when textarea loses focus */
        onBlur: PropTypes.func
    };

    static defaultProps = {
        autoExpand: false,
        srOnlyLabel: false
    };

    constructor(props) {
        super(props);

        this.state = {
            isDirty: false,
            hasFocus: false,
            minRows: this.props.rows || 0
        };

        this.debouncedResize = debounce(this.onResize, 250, {
            maxWait: 1000
        }).bind(this);

        // stored if autoExpand and (maxRows or maxHeight) configured for use.
        // is used to toggle overflow from hidden to auto when max height is reached
        this.computedMaxHeight = 0;
    }

    componentDidMount() {
        if (this.textArea && this.props.autoExpand) {
            this.initializeAutoExpand({
                maxHeight: this.props.maxHeight,
                minRows: this.state.minRows,
                maxRows: this.props.maxRows
            });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.isDirty !== !!this.props.value) {
            this.setState({isDirty: !!this.props.value});
        }

        const valueChanged = this.props.value !== prevProps.value;
        if (this.props.autoExpand && valueChanged) {
            this.autoExpand();
        }
    }

    componentWillUnmount() {
        if (this.props.autoExpand && typeof window !== 'undefined') {
            window.removeEventListener('resize', this.debouncedResize);
        }
    }

    onResize() {
        this.autoExpand();
    }

    /* eslint-disable complexity */
    autoExpand() {
        // Issue #104 - FormTextArea referring to undefined ref on resize
        if (!this.textArea) {
            return false;
        }

        // Set the height to 0, so we can get the correct content height via scrollHeight
        // Also, running this through state changes causes a race condition when decreasing scrollHeight.
        this.textArea.style.height = '0px';

        // set height to scrollHeight, but add border-width * 2 since that is not reported in scrollHeight
        // but is used in box-sizing: border-box to determine height of textarea element
        const newHeight =
            this.textArea.scrollHeight +
            this.computedStyleValues.borderWidth * 2;
        this.textArea.style.height = `${newHeight}px`;

        // if computedMaxHeight > 0, check to make sure overflow is appropriate
        if (this.computedMaxHeight > 0) {
            const overflow = this.textArea.style.overflow;
            if (overflow === 'hidden' && newHeight > this.computedMaxHeight) {
                this.textArea.style.overflow = 'auto';
            } else if (
                overflow === 'auto' &&
                newHeight < this.computedMaxHeight
            ) {
                this.textArea.style.overflow = 'hidden';
            }
        }

        return true;
    }
    /* eslint-enable complexity */

    handleChange = (event) => {
        if (this.state.isDirty !== !!event.target.value) {
            this.setState({isDirty: !!event.target.value});
        }

        // controlled components will autoExpand via componentDidUpdate
        if (this.props.autoExpand && typeof this.props.value !== 'string') {
            this.autoExpand();
        }

        if (this.props.onChange) {
            this.props.onChange(event);
        }
    };

    handleFocus = (event) => {
        if (!this.state.hasFocus) {
            this.setState({hasFocus: true});
        }

        if (this.props.onFocus) {
            this.props.onFocus(event);
        }
    };

    handleBlur = (event) => {
        if (this.state.hasFocus) {
            this.setState({hasFocus: false});
        }

        if (this.props.onBlur) {
            this.props.onBlur(event);
        }
    };

    // eslint-disable-next-line complexity
    initializeAutoExpand({maxHeight = 'none', minRows = 0, maxRows = 0}) {
        if (typeof window !== 'undefined') {
            window.addEventListener('resize', this.debouncedResize);
        }

        if (maxHeight) {
            this.textArea.style.maxHeight = maxHeight;
        }

        // set this.computedStyleValues with computed styles, passing in DEFAULT_COMPUTED_STYLE_VALUES
        // as default values. do this after setting maxHeight, so we can get a computed max-height, if set.
        this.computedStyleValues = this.getComputedStyleValues(
            DEFAULT_COMPUTED_STYLE_VALUES
        );

        if (!isNaN(this.computedStyleValues.maxHeight)) {
            this.computedMaxHeight = this.computedStyleValues.maxHeight;
        }

        if (minRows > 1) {
            this.textArea.style.minHeight = `${this.getHeightFromRows(
                minRows,
                this.computedStyleValues
            )}px`;
        }

        if (!this.props.maxHeight && maxRows > 0) {
            const maxHeightFromRows = this.getHeightFromRows(
                maxRows,
                this.computedStyleValues
            );
            this.textArea.style.maxHeight = `${maxHeightFromRows}px`;
            this.computedMaxHeight = maxHeightFromRows;
        }

        this.textArea.style.overflow = 'hidden';
        this.autoExpand();
    }

    getHeightFromRows(rows, computedStyleValues) {
        return (
            rows * computedStyleValues.lineHeight +
            computedStyleValues.paddingTop +
            computedStyleValues.paddingBottom +
            computedStyleValues.borderWidth * 2
        );
    }

    getComputedStyleValues(defaults) {
        if (this.textArea && typeof window !== 'undefined') {
            const computedStyle = window.getComputedStyle(this.textArea);
            return {
                borderWidth: this.getStyleValue(
                    'border-width',
                    computedStyle,
                    defaults.borderWidth
                ),
                lineHeight: this.getStyleValue(
                    'line-height',
                    computedStyle,
                    defaults.lineHeight
                ),
                maxHeight: this.getStyleValue('max-height', computedStyle, NaN),
                paddingBottom: this.getStyleValue(
                    'padding-bottom',
                    computedStyle,
                    defaults.paddingBottom
                ),
                paddingTop: this.getStyleValue(
                    'padding-top',
                    computedStyle,
                    defaults.paddingTop
                )
            };
        }
        return defaults;
    }

    getStyleValue(property, computedStyle, defaultValue) {
        const value = parseInt(computedStyle.getPropertyValue(property), 10);
        return !isNaN(value) ? value : defaultValue;
    }

    /** Can be called exernally to force focus */
    focus() {
        this.textArea.focus();
    }

    render() {
        const {
            label,
            className,
            helpText,
            inverse,
            errorMsg,
            srOnlyLabel,
            autoExpand,
            ...rest
        } = this.props;

        const hasError = !!errorMsg;
        const notEmpty = this.state.isDirty || this.props.value;

        const parentClasses = classNames(className, {
            'form-group': true,
            'floating-label': !srOnlyLabel,
            'not-empty': notEmpty,
            empty: !notEmpty,
            'has-error': hasError
        });

        // note that below, the disabled prop has not been destructured since it needs to
        // get passed through with ...rest to the textarea element to be used as an attribute
        const labelClasses = classNames('FormTextArea__floating-label', {
            'sr-only': srOnlyLabel,
            'FormTextArea__floating-label--has-error':
                hasError && !this.state.hasFocus,
            'FormTextArea__floating-label--disabled': this.props.disabled
        });

        const textAreaClasses = classNames(
            'FormTextArea__textarea form-control',
            {
                'FormTextArea__textarea--auto-expand': autoExpand
            }
        );

        const message = errorMsg || helpText;
        const messageId = `${this.props.id}__help`;

        let textareaProps = {
            className: textAreaClasses,
            ref: (c) => {
                this.textArea = c;
            },
            onChange: this.handleChange,
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            ...(typeof label === 'string' && {
                'aria-label': label,
                placeholder: label
            }),
            ...(message && {'aria-describedby': messageId}),
            ...rest
        };

        let helpProps = {
            className: classNames('help-block', {
                'text-white': inverse
            }),
            ...(errorMsg && {role: 'alert', 'aria-live': 'polite'})
        };

        // These props are invalid in the native textarea, so pop them off.
        delete textareaProps.maxHeight;
        delete textareaProps.maxRows;

        return (
            <div className={parentClasses}>
                <textarea {...textareaProps} />

                <label className={labelClasses} htmlFor={this.props.id}>
                    {label}
                </label>

                {message && typeof message === 'string' && (
                    <span
                        {...helpProps}
                        id={messageId}
                        dangerouslySetInnerHTML={{__html: message}}
                    />
                )}

                {message && typeof message !== 'string' && (
                    <div {...helpProps} id={messageId}>
                        {message}
                    </div>
                )}
            </div>
        );
    }
}

export default FormTextArea;
